import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SocketService } from '@frontend/common/util';
import { merge, Observable, of, race, throwError } from 'rxjs';
import { catchError, filter, first, ignoreElements, mergeMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { Kiosk } from '../kiosk/kiosk.model';

@Injectable({
  providedIn: 'root',
})
export class KioskApiService {
  constructor(private http: HttpClient, private socketSrv: SocketService) {}

  public create(entity: Kiosk, isCA?: boolean) {
    const correlation_id = entity.id;
    return merge(
      this.http.post(`${isCA ? 'http://ca/' : ''}kiosks`, { ...entity, correlation_id }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(
            mergeMap((message) => throwError({ id: entity.id, message }))
          )
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('kiosks.create')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('kiosks.create').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError({ id: entity.id, message }))
        )
      ).pipe(first())
    );
  }

  public delete(id, isCA?: boolean) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http.request('delete', `${isCA ? 'http://ca/' : ''}kiosks/${id}`, { body: { correlation_id } }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(mergeMap((message) => throwError({ id, message })))
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('kiosks.remove')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('kiosks.remove').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError({ id, message }))
        )
      ).pipe(first())
    );
  }

  public get(params?: { [key: string]: string | string[] }, isCA?: boolean): Observable<Kiosk[]> {
    return this.http.get<Kiosk[]>(`${isCA ? 'http://ca/' : ''}kiosks`, { params });
  }

  public getOne(id, isCA?: boolean) {
    return this.http.get<Kiosk>(`${isCA ? 'http://ca/' : ''}kiosks/${id}`);
  }

  public update(entity, isCA?: boolean) {
    const correlation_id = 'correlation|' + uuid();
    return merge(
      this.http.put(`${isCA ? 'http://ca/' : ''}kiosks/${entity.id}`, { ...entity, correlation_id }).pipe(
        ignoreElements(),
        catchError((err) =>
          of(err?.error?.message).pipe(
            mergeMap((message) => throwError({ id: entity.id, message }))
          )
        )
      ),
      race(
        this.socketSrv
          .fromLocalSuccess<any>('kiosks.update')
          .pipe(filter((ws) => ws.correlation_id === correlation_id)),
        this.socketSrv.fromLocalError('kiosks.update').pipe(
          filter((ws) => ws.correlation_id === correlation_id),
          mergeMap(({ message }) => throwError({ id: entity.id, message }))
        )
      ).pipe(first())
    );
  }
}
